/**
=========================================================
* Trust Cargo - Customers
=========================================================

* The customer records themselves (code, VAT name, address). The people
* behind them live on the Users page, which shows both together; this list is
* the customer side on its own.
*
* Adding and editing both happen in a dialog over this list.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";
import axios from "axios";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api";

function Customers() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API}/customers`);
      const data = await response.json();
      setRows(data.customers || []);
    } catch (e) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const filteredRows = filterRows(rows, query);
  const pager = usePagination(filteredRows);

  const removeOne = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete customer ${row.name}?`)) return;

    try {
      await axios.post(
        `${API}/remove_customer`,
        { id: row.id },
        { headers: { Accept: "application/json" } }
      );
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
  };

  const cell = { fontSize: 14 };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="Customers"
              value={query}
              onChange={setQuery}
              count={filteredRows.length}
              total={rows.length}
              placeholder="Search by name, code or phone..."
              actions={
                <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                  Add Customer
                </Button>
              }
            />

            {loading ? (
              <CargoLoader label="Loading customers" />
            ) : (
              <>
                <SuiBox sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 900 }} aria-label="customers table">
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Name</TableCell>
                        <TableCell align="center">Code</TableCell>
                        <TableCell align="center">Phone Number</TableCell>
                        <TableCell align="center">Address</TableCell>
                        <TableCell align="center">Vat Name</TableCell>
                        <TableCell align="center">Operations</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pager.pageRows.map((row) => (
                        <TableRow key={row.id}>
                          <IdCell value={row.id} />
                          <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                            {row.name ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.code ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.phone_number ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.address ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.vat_name ?? "-"}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                {
                                  icon: "edit",
                                  label: "Edit customer",
                                  onClick: () => setEditingId(row.id),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete",
                                  tone: "danger",
                                  onClick: () => removeOne(row),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No customers
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SuiBox>
                <Pagination {...pager} />
              </>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      <AddDialog
        open={adding}
        entity="customer"
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      {/* same dialog, loaded with the existing customer */}
      <AddDialog
        open={Boolean(editingId)}
        entity="customer"
        recordId={editingId}
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Customers;
